import axios from 'axios';
import { parseEmotionResponse } from './parseEmotionResponse';
// Optionally, you can import a delay helper or create one:
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

class EmotionClassifier {
  constructor(options = {}) {
    // Threshold for the emotion classification
    // It  can be between 0 -> 100
    this.threshold = options.threshold || 95;
    this.model = options.model || 'claude-3-5-sonnet-20241022';
    
    // When forcing JSON response, we use a shorter max_tokens value for speed.
    this.forcedMaxTokens = options.forcedMaxTokens || 2048;
    this.normalMaxTokens = options.normalMaxTokens || 2048;
    this.conclusiveMaxTokens = options.conclusiveMaxTokens || 2048;
    
    this.jsonPrefillText = `Out of 220 feelings in the <feelings> tag, the sentiment of this user best fits a single feeling represented as a JSON object with "confidence (integer between 0->100)", "id", "name", and "description" fields. Please respond with just the JSON object:
              {   
                  "confidence":`;
    this.conclusivePrefill = `This turn is the time to get conclusive. Do not encourage user to sidestep or restart the discussion. Consider everything that the user has shared so far and if it could point to a specific feeling from the <feelings> tag:

    If confidence is >80: Gently note that you are approaching the end of the conversation. Make a firm statement with light validation and confirmation question.
    
    If confidence is <80: Gently note that you are approaching the end of the conversation. Ask confirmation question on your soft conclusion.`;
    this.forceConclusivePrefill = `User inputted the last possible input. Now conclude the conversation on next step. If we couldn't get a granular \
      feeling, we should share this with user and encourage for another try. \
      Unless, if there's a crisis response, we should act accordingly, as defined in the system prompt.\
      
      <thinking>
      The concluding text should be the following (unless its a crisis response):
      "I aim for us to get granular in limited number of coversation turns. Since we could not identify the specific feeling this time, please restart the conversation when you wish to try again."
      </thinking>`;

    this.retryAttempts = options.retryAttempts || 3; // maximum retries
  }

  async _apiCallWithRetry(payload) {
    let attempt = 0;
    while (attempt < this.retryAttempts) {
      try {
        const res = await axios.post('/api/messages', payload);
        if (res.data && res.data.content) {
          return res;
        } else {
          throw new Error('No response content received');
        }
      } catch (error) {
        attempt++;
        console.warn(`Attempt ${attempt} failed:`, error.response?.data || error.message);
        // For gateway timeouts (504) or other transient issues, wait before retrying
        if (attempt < this.retryAttempts) {
          await delay(1000 * attempt); // exponential delay (1s, 2s, 3s, …)
        } else {
          throw error;
        }
      }
    }
  }

  async tryForcedClassification(input, messages, selectedRole) {
    // Build conversation history properly
    const conversationHistory = messages.map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'assistant',
      content: msg.text
    }));

    // Add the current user input and the JSON instruction
    const forceRequestMessages = [
      ...conversationHistory,
      { role: 'user', content: input },
      { role: 'assistant', content: this.jsonPrefillText }
    ];

    console.log('Forcing JSON classification...');
    console.log(forceRequestMessages);
    console.log("-------------------------------");

    try {
      const payload = {
        model: this.model,
        messages: forceRequestMessages,
        role: selectedRole,
        max_tokens: this.forcedMaxTokens
      };
      const res = await this._apiCallWithRetry(payload);
      const aiResponse = res.data.content.trim();
      const emotionData = parseEmotionResponse(aiResponse);
      if (emotionData && emotionData.confidence >= this.threshold) {
        return { 
          emotionData, 
          requestMessages: forceRequestMessages,
          introMessage: 'Getting granular!'
        };
      }
    } catch (error) {
      console.error('Forced classification error:', error);
    }
    return null;
  }

  async normalClassification(input, messages, selectedRole, prefillType) {
    // Validate messages array
    if (!Array.isArray(messages) || !messages.every(msg => msg && msg.text)) {
      console.warn('Invalid messages format detected');
      messages = messages.filter(msg => msg && msg.text);
    }

    const conversationHistory = messages.map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'assistant', 
      content: msg.text || ''
    }));

    let normalRequestMessages;

    if (prefillType === 'conclusive') {
      normalRequestMessages = [
        ...conversationHistory,
        { role: 'user', content: input || '' },
        { 
          role: 'assistant', 
          content: `${this.conclusivePrefill} Therefore, I will focus on validation and confirmation.` 
        }
      ];
    } else if (prefillType === 'force_conclusive') {
      normalRequestMessages = [
        ...conversationHistory,
        { role: 'user', content: input || '' },
        { role: 'assistant', content: this.forceConclusivePrefill }
      ];
    }
    else {
      normalRequestMessages = [
        ...conversationHistory,
        { role: 'user', content: input || '' }
      ];
    }

    console.log('Normal classification, prefill:', prefillType);
    console.log(normalRequestMessages);
    console.log("-------------------------------");

    try {
      const payload = {
        model: this.model,
        messages: normalRequestMessages,
        role: selectedRole,
        max_tokens: prefillType === 'conclusive'
          ? this.conclusiveMaxTokens  // use lower max_tokens for conclusive
          : this.normalMaxTokens
      };
      const res = await this._apiCallWithRetry(payload);
      return { 
        responseData: res.data, 
        requestMessages: normalRequestMessages 
      };
    } catch (error) {
      console.error('Normal classification error:', error.response?.data || error);
      throw error;
    }
  }
}

export default EmotionClassifier;